import { createPortal } from 'react-dom';
import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { PartiallyGuardedPlayerPositionManager } from './PartiallyGuardedPlayerPositionManager';
import type { PlayerPositionManagerArgs } from './usePlayerPositionManager';

export type PlayerPositionManagerProps<TPlayerParentContainer> =
  PlayerPositionManagerArgs<TPlayerParentContainer>;

export const PlayerPositionManager = <TPlayerParentContainer extends HTMLElement>({
  bottomStyleWhenStickyAtBottomRight,
  leftStyleWhenStickyAtTop,
  player,
  playerParentContainer,
  rightStyleWhenStickyAtBottomRight,
  widthStyleWhenStickyAtBottomRight,
  rightStyleWhenStickyAtTop,
  topStyleWhenStickyAtTop,
}: PlayerPositionManagerProps<TPlayerParentContainer>): JSX.Element | null => {
  const intersectionObserverTargetRef = useRef<HTMLDivElement | null>(null);
  const nonStickyPlayerPhantomRef = useRef<HTMLDivElement | null>(null);

  const [_, setStateToForceRender] = useState(0);

  useEffect(() => {
    setStateToForceRender(1);
  }, []);

  if (player == null) {
    return null;
  }

  if (playerParentContainer == null) {
    return null;
  }

  const portalTarget = playerParentContainer.parentElement;
  if (portalTarget == null) {
    return null;
  }

  return (
    <>
      {createPortal(
        <div
          ref={nonStickyPlayerPhantomRef}
          className="non-sticky-player-phantom"
          style={{
            position: 'relative',
          }}
        >
          <div
            ref={intersectionObserverTargetRef}
            className="intersection-observer-target"
            data-testid="intersection-observer-target"
            style={{
              bottom: 0,
              left: 0,
              position: 'absolute',
              right: 0,
              zIndex: -1000,
            }}
          />
        </div>,
        portalTarget,
      )}
      <PartiallyGuardedPlayerPositionManager
        bottomStyleWhenStickyAtBottomRight={bottomStyleWhenStickyAtBottomRight}
        intersectionObserverTarget={intersectionObserverTargetRef.current}
        leftStyleWhenStickyAtTop={leftStyleWhenStickyAtTop}
        nonStickyPlayerPhantom={nonStickyPlayerPhantomRef.current}
        player={player}
        playerParentContainer={playerParentContainer}
        rightStyleWhenStickyAtBottomRight={rightStyleWhenStickyAtBottomRight}
        rightStyleWhenStickyAtTop={rightStyleWhenStickyAtTop}
        topStyleWhenStickyAtTop={topStyleWhenStickyAtTop}
        widthStyleWhenStickyAtBottomRight={widthStyleWhenStickyAtBottomRight}
      />
    </>
  );
};
